import { useCallback } from 'react';
import * as Cesium from 'cesium';
import type { Strategy } from '../../../types/mission';
import type { KeplerianOrbitParams } from '../../../lib/orbitPropagator';
import { buildSampledPosition, buildKeplerianSampledPosition } from '../../../lib/orbitPropagator';
import { ACCENT, BLACK, PREVIEW_COLOR, MU, previewPoint } from '../constants';
import { buildTleSampledPosition } from '../utils/orbitUtils';

export function useOrbitPreview(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  layersRef: React.MutableRefObject<any>,
  orbitLeadTimeRef: React.MutableRefObject<number>,
  orbitTrailTimeRef: React.MutableRefObject<number>,
  clearGroup: (group: string) => void,
) {
  const showOrbitPreview = useCallback((orbits: KeplerianOrbitParams[]) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    clearGroup('__orbit_preview__');

    const now = Cesium.JulianDate.toDate(v.clock.startTime);
    const ids: string[] = [];

    orbits.forEach((op, idx) => {
      try {
        const n = Math.sqrt(MU / Math.pow(op.semiMajorAxis, 3));
        const T = (2 * Math.PI) / n;

        const previewStart = new Date(now.getTime() - T * 2 * 1000);
        const sampledPos = buildKeplerianSampledPosition(op, now, 4, 480);

        sampledPos.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        sampledPos.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;

        const entity = ds.entities.add({
          id: `__preview_${idx}__${Date.now()}`,
          position: sampledPos,
          orientation: new Cesium.VelocityOrientationProperty(sampledPos),
          point: previewPoint,
          path: {
            show: true,
            material: new Cesium.ColorMaterialProperty(PREVIEW_COLOR),
            width: 0.8,
            leadTime: T,
            trailTime: 0,
            resolution: 120,
          },
        });

        if (entity.id) ids.push(entity.id);
      } catch (e) {
        console.error('[useOrbitPreview.showOrbitPreview] error creating orbit', idx, e);
      }
    });

    entityGroupMap.current.set('__orbit_preview__', ids);
  }, [clearGroup]);

  const showTleOrbitPreview = useCallback((entries: { line1: string; line2: string }[]) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    clearGroup('__orbit_preview__');

    const now = Cesium.JulianDate.toDate(v.clock.startTime);
    const ids: string[] = [];

    entries.forEach(({ line1, line2 }, idx) => {
      try {
        const previewStart = new Date(now.getTime() - 5400 * 1_000);
        const { position: sampledPos, periodSec: T } = buildTleSampledPosition(
          line1, line2, previewStart, 4, 120,
        );

        const entity = ds.entities.add({
          id: `__preview_tle_${idx}__${Date.now()}`,
          position: sampledPos,
          point: previewPoint,
          path: {
            show: true,
            material: new Cesium.ColorMaterialProperty(PREVIEW_COLOR),
            width: 0.8,
            leadTime: T,
            trailTime: 0,
            resolution: 120,
          },
        });

        if (entity.id) ids.push(entity.id);
      } catch (e) {
        console.error('[useOrbitPreview.showTleOrbitPreview] error', idx, e);
      }
    });

    entityGroupMap.current.set('__orbit_preview__', ids);
  }, [clearGroup]);

  const clearOrbitPreview = useCallback(() => {
    clearGroup('__orbit_preview__');
  }, [clearGroup]);

  const drawOrbits = useCallback((params: Strategy) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    clearGroup('constellation');

    const { planes, spp, alt, inc } = params;
    const ids: string[] = [];

    for (let p = 0; p < planes; p++) {
      for (let s = 0; s < spp; s++) {
        const raanDeg = (p / planes) * 360;
        const M0Deg = ((s / spp) + (p / planes) * 0.10) * 360;
        const sampledPos = buildSampledPosition({ altKm: alt, incDeg: inc, raanDeg, M0Deg });

        const entity = ds.entities.add({
          position: sampledPos,
          point: {
            pixelSize: 4,
            color: ACCENT,
            outlineColor: BLACK,
            outlineWidth: 0.7,
            show: new Cesium.CallbackProperty(() => layersRef.current.satellites, false),
          },
        });
        if (entity.id) ids.push(entity.id);
      }
    }
    entityGroupMap.current.set('constellation', ids);
  }, [clearGroup]);

  return {
    showOrbitPreview,
    showTleOrbitPreview,
    clearOrbitPreview,
    drawOrbits,
  };
}
